/**
 * `CodeCallCard` — the raw request/response row for one phase.
 * Extracted VERBATIM from the pre-ADR-0163 `EndpointColumn` row
 * rendering (method / path / status / ms / documented-UA panel /
 * `<pre>` body). Code mode preserves full fidelity; the honesty
 * elements (LIVE / ILLUSTRATION tag, masquerade note) render exactly
 * as before.
 */

import { motion } from 'framer-motion';
import { Radio, ShieldAlert } from 'lucide-react';

import { Pill } from '../../../components/atoms/index.js';
import { useC } from '../../../lib/theme.js';
import type { Phase } from '../journeys/types.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface CodeCallCardProps {
  phase: Phase;
  /**
   * When rendered inline under a human card (the `‹› Show raw` peek),
   * the outer card already carries the border/background — skip the
   * container chrome and the entrance animation.
   */
  inline?: boolean;
}

export function CodeCallCard({ phase: p, inline = false }: CodeCallCardProps): JSX.Element {
  const C = useC();
  const status = p.endpoint.status;
  const pending = status === 0;
  const ok = status >= 200 && status < 400;

  const body = (
    <>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <Pill tone={p.endpoint.method === 'GET' ? 'sky' : 'mint'}>{p.endpoint.method}</Pill>
        <span style={{ fontFamily: monoFont, fontSize: 11, color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.endpoint.path}</span>
        {p.endpoint.live ? (
          <span className="flex items-center gap-1" style={{ fontFamily: monoFont, fontSize: 8, color: C.limeFg, letterSpacing: 1 }}>
            <Radio size={9} color={C.limeFg} /> LIVE
          </span>
        ) : (
          <span style={{ fontFamily: monoFont, fontSize: 8, color: C.dim, letterSpacing: 1 }}>ILLUSTRATION</span>
        )}
        <Pill tone={pending ? 'sky' : ok ? 'lime' : 'coral'}>{pending ? '…' : String(status)}</Pill>
        <span style={{ fontFamily: monoFont, fontSize: 10, color: C.dim }}>{pending || p.endpoint.ms === 0 ? '' : `${p.endpoint.ms}ms`}</span>
      </div>
      {p.endpoint.userAgent !== undefined && (
        <div
          className="mb-2 px-2 py-1.5"
          style={{
            background: C.surface,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 2,
          }}
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 1,
              marginBottom: 3,
              textTransform: 'uppercase',
            }}
          >
            {p.endpoint.userAgentCaption ?? 'User-Agent · documented signature'}
          </div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.text,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            <span style={{ color: C.muted }}>User-Agent: </span>
            {p.endpoint.userAgent}
          </div>
          {p.endpoint.masqueradeNote !== undefined && p.endpoint.masqueradeNote !== null && (
            <div
              className="flex items-center gap-1.5 mt-1.5"
              style={{
                fontFamily: sansFont,
                fontSize: 10,
                color: C.amberFg,
                lineHeight: 1.4,
              }}
            >
              <ShieldAlert size={10} color={C.amberFg} className="flex-shrink-0" />
              <span>{p.endpoint.masqueradeNote}</span>
            </div>
          )}
        </div>
      )}
      <pre style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{p.endpoint.body}</pre>
    </>
  );

  if (inline) {
    return (
      <div className="pl-3 pt-2 mt-2" style={{ borderTop: `1px solid ${C.borderSoft}` }}>
        {body}
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-3"
      style={{ background: C.surface2, border: `1px solid ${!pending && !ok ? C.coralFg : C.border}`, borderRadius: 2 }}
    >
      {body}
    </motion.div>
  );
}
